// The presenter: draws StudioState in a room. It owns the live objects
// (devices, game cases, the laptop screen), turns pointer and key input into
// core intents or device input, and animates between focus states.

import * as THREE from "three";
import type { Stage } from "../stage/stage.ts";
import { DeviceActor, LAYER_INPUT, LAYER_SELECT, type StagePart } from "../stage/device.ts";
import { CaseActor, CASE_H } from "../stage/case.ts";
import { LaptopScreen } from "../stage/laptop.ts";
import { TvScreen } from "../stage/tv.ts";
import { Station, TV_DENSITY } from "./station.ts";
import { makeGlow } from "../stage/room.ts";
import { poseFrom, type Pose } from "../stage/rig.ts";
import { ease } from "../stage/tween.ts";
import { BUTTON_BITS, DeviceRealm, KEY_BITS, type RunningRealm } from "../emu/realm.ts";
import { PaymentRequired, Refused, SignInRequired, type Focus, type StudioCore, type StudioState } from "../core/studio.ts";
import { DEVICE_IDS, type AppBuild, type AppInfo, type DeviceId, type NativeTarget } from "../core/types.ts";
import { pageCount, pageOf, pageSlice, shelfOrder, shelfStep, type ShelfStep } from "../core/shelf.ts";
import { noTouchHint, quietHint } from "../core/touch.ts";
import type { Wanted } from "../core/wall.ts";
import { track } from "../api/events.ts";
import { fetchApp } from "../api/http.ts";
import { CUSTOM_SKIN, deviceEntry } from "../core/catalog.ts";
import { lang, t } from "../ui/lang.ts";
import { deviceName, deviceShort } from "../ui/names.ts";
import { GUIDE_PACKAGE, guideUrl } from "../core/install-guide.ts";
import type { DeviceAnchor } from "../room/contract.ts";
import type { Overlay } from "../ui/overlay.ts";
import { price } from "../ui/dom.ts";
import type { StudioDesktop } from "../shell/system-host.ts";

export interface ViewOptions {
  devicesUrl: string;
  runtimeUrl: string;
  desktopUrl: string;
  tvUrl: string;
}

/** A turn of the rack: how far a case slides in, in metres, and for how long. Each column starts a little after the one before. */
const SHELF_SLIDE = 0.07;
const SHELF_TURN_MS = 220;
const SHELF_STAGGER_MS = 24;
/** How far under the lowest row of cases the rack's pager sits, in metres. */
const SHELF_PAGER_DROP = 0.075;
/** The keys that move along the rack beside a game's card. */
const SHELF_KEYS: Record<string, ShelfStep> = { ArrowLeft: "previous", ArrowRight: "next", ArrowUp: "up", ArrowDown: "down" };

type Target = { device: DeviceActor } | { game: CaseActor } | { laptop: true } | { tv: true };


interface Press {
  pointerId: number;
  part: StagePart;
  bit: number;
  tick: number;
}

function sameFocus(a: Focus, b: Focus): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export class StudioView {
  private devices = new Map<DeviceId, DeviceActor>();
  /** The cases on the rack now: one page of the games (core/shelf.ts). */
  private cases = new Map<string, CaseActor>();
  /** Every case that is made: the page on the rack and the pages beside it, so a turn shows covers at once. */
  private made = new Map<string, CaseActor>();
  private shelfPage = 0;
  private shelfPages = 1;
  private laptop!: LaptopScreen;
  private tv!: TvScreen;
  private station: Station | null = null;
  private desktop: StudioDesktop | null = null;
  private desktopDirty = false;
  private realm: RunningRealm | null = null;
  private realmKey = "";
  private raycaster = new THREE.Raycaster();
  private hovered: Target | null = null;
  private press: Press | null = null;
  /** How far each device is held off its support right now: up and out. */
  private holds = new Map<DeviceActor, { up: number; out: number }>();
  private pulls = new Map<CaseActor, number>();
  private skins = new Map<DeviceId, string>();
  private generation = 0;
  /** A session is being asked for: a second press of the same button waits for the first. */
  private creating = false;
  private laptopDown = false;
  /** A press on the room that has not yet become a click or a drag. */
  private drag: { pointerId: number; x: number; y: number; moved: boolean } | null = null;
  /** Devices that have left their shelf slot, or are on their way back to it. */
  private away = new Set<DeviceActor>();
  private keyTicks = new Map<string, number>();
  /** Sum over every caster's pose; a change means the shadow maps are stale. */
  private castStamp = Number.NaN;
  private clock = 0;
  /** Says that a screen takes no touch, and then not again for a few seconds. */
  private touchHint = quietHint();

  constructor(private stage: Stage, private core: StudioCore, private overlay: Overlay, private options: ViewOptions) {}

  private get room() {
    return this.stage.room!.manifest;
  }

  async init(): Promise<void> {
    const room = this.room;
    await Promise.all(DEVICE_IDS.map(async (id, index) => {
      try {
        const actor = await DeviceActor.load(
          id, `${this.options.devicesUrl}${id}/`, room.deviceScale, deviceName(id), deviceEntry(id).rests, room.anchors.playSpot.tilt,
        );
        actor.place(room.anchors.deviceSlots[index]!);
        this.stage.actors.add(actor.root);
        this.devices.set(id, actor);
      } catch (error) {
        console.warn(`device ${id} is unavailable`, error);
      }
    }));
    this.laptop = new LaptopScreen(room);
    this.stage.scene.add(this.laptop.mesh, this.laptop.hitbox);
    this.tv = new TvScreen(room, TV_DENSITY);
    this.stage.scene.add(this.tv.mesh, this.tv.hitbox);
    this.syncSnapshot(this.core.state);
    void this.bootDesktop();
    void this.bootTv();

    const canvas = this.stage.canvas;
    canvas.addEventListener("pointermove", (event) => this.onPointerMove(event));
    canvas.addEventListener("pointerdown", (event) => this.onPointerDown(event));
    canvas.addEventListener("pointerup", (event) => this.onPointerUp(event));
    canvas.addEventListener("pointercancel", (event) => this.onPointerUp(event));
    canvas.addEventListener("pointerleave", () => this.setHover(null));
    canvas.addEventListener("dblclick", (event) => {
      if (this.core.state.focus.kind === "room" && !this.targetAt(event as PointerEvent)) this.stage.rig.reset();
    });
    canvas.addEventListener("wheel", (event) => {
      const kind = this.core.state.focus.kind;
      if (kind === "laptop" && this.desktop) {
        this.desktop.wheel(event.deltaY);
      } else if (kind === "room") {
        // Scroll and pinch (which arrives as a wheel with ctrlKey) zoom toward the pointer.
        const rect = canvas.getBoundingClientRect();
        const step = event.ctrlKey ? event.deltaY * 0.012 : event.deltaY * 0.0016;
        this.stage.rig.zoom(Math.exp(step), ((event.clientX - rect.left) / rect.width) * 2 - 1, ((event.clientY - rect.top) / rect.height) * 2 - 1);
        this.setHover(null);
      } else {
        return;
      }
      event.preventDefault();
    }, { passive: false });
    canvas.addEventListener("contextmenu", (event) => event.preventDefault());
    window.addEventListener("keydown", (event) => this.onKey(event, true));
    window.addEventListener("keyup", (event) => this.onKey(event, false));
    window.addEventListener("blur", () => {
      this.realm?.release();
      this.desktop?.blur();
    });

    this.core.subscribe((state, previous) => this.onState(state, previous));
    this.stage.onFrame((dt) => this.frame(dt));
    this.overlay.render(this.core.state);
  }

  // ------------------------------------------------------------- snapshot

  private syncSnapshot(state: StudioState): void {
    for (const info of state.snapshot.devices) {
      const actor = this.devices.get(info.id);
      // What the device wears: a skin, and under the custom skin its colour.
      const wears = `${info.skin}|${info.color ?? ""}`;
      if (!actor || this.skins.get(info.id) === wears) continue;
      try {
        actor.setSkin(info.skin, info.color);
        this.skins.set(info.id, wears);
      } catch (error) {
        console.warn(error);
      }
    }
    this.syncCases(state.snapshot.apps);
    this.station?.setFeed(state.snapshot.apps, state.snapshot.devices, state.snapshot.aired);
    this.desktop?.publish({ t: "studio", snapshot: state.snapshot, focus: this.focusLine(state.focus), lang: lang() });
  }

  private focusLine(focus: Focus) {
    // The laptop knows nothing of the chat: to it, the visitor is in the room.
    if (focus.kind === "make") return { kind: "room" as const, device: undefined, app: undefined };
    return {
      kind: focus.kind,
      device: "device" in focus ? focus.device : undefined,
      app: "app" in focus ? focus.app : undefined,
    };
  }

  /**
   * Put the rack's current page of games on its slots. The pages before and
   * after are made too and kept off the stage; every other case is let go.
   */
  private syncCases(apps: AppInfo[]): void {
    const slots = this.room.anchors.appSlots;
    const ordered = shelfOrder(apps);
    this.shelfPages = pageCount(ordered.length, slots.length);
    this.shelfPage = Math.min(this.shelfPage, this.shelfPages - 1);
    const page = (at: number) => (at < this.shelfPages ? pageSlice(ordered, at, slots.length) : []);
    const shown = page(this.shelfPage);
    const beside = [...page(this.shelfPage - 1), ...page(this.shelfPage + 1)];
    const keep = new Set([...shown, ...beside].map((app) => app.id));
    for (const [id, actor] of this.made) {
      if (keep.has(id)) continue;
      this.stage.actors.remove(actor.root);
      this.pulls.delete(actor);
      actor.dispose();
      this.made.delete(id);
    }
    const caseOf = (app: AppInfo): CaseActor => {
      let actor = this.made.get(app.id);
      if (actor) actor.update(app);
      else this.made.set(app.id, (actor = new CaseActor(app)));
      return actor;
    };
    this.cases.clear();
    shown.forEach((app, index) => {
      const actor = caseOf(app);
      actor.place(slots[index]!);
      actor.root.scale.setScalar(1);
      if (!actor.root.parent) this.stage.actors.add(actor.root);
      this.cases.set(app.id, actor);
    });
    for (const app of beside) {
      const actor = caseOf(app);
      this.stage.actors.remove(actor.root);
      this.pulls.delete(actor);
    }
    if (this.hovered && "game" in this.hovered && !this.cases.has(this.hovered.game.app.id)) this.setHover(null);
    this.overlay.shelf(this.shelfPage, this.shelfPages);
  }

  /**
   * Show another page of the rack. The cases slide in a short way from the
   * side the page was on, unless the visitor asked for less motion. With a
   * game's card open, the card moves to the game in the same place on the
   * new page, so the pages can be leafed through from a card.
   */
  private showPage(page: number, moving = true): void {
    const next = Math.max(0, Math.min(this.shelfPages - 1, page));
    if (next === this.shelfPage) return;
    const side = next > this.shelfPage ? 1 : -1;
    const focus = this.core.state.focus;
    const held = focus.kind === "app" ? [...this.cases.keys()].indexOf(focus.app) : -1;
    this.shelfPage = next;
    this.syncCases(this.core.state.snapshot.apps);
    const slots = this.room.anchors.appSlots;
    const columns = slots.filter((slot) => slot.position[1] === slots[0]!.position[1]).length;
    const still = !moving || matchMedia("(prefers-reduced-motion: reduce)").matches;
    [...this.cases.values()].forEach((actor, index) => {
      const slot = slots[index]!;
      if (still) return;
      const along = new THREE.Vector3(Math.cos(slot.yaw), 0, -Math.sin(slot.yaw)).multiplyScalar(side * SHELF_SLIDE);
      const start = new THREE.Vector3().fromArray(slot.position).add(along);
      const end = new THREE.Vector3().fromArray(slot.position);
      actor.root.position.copy(start);
      actor.root.scale.setScalar(0.9);
      void this.stage.tweens.run(SHELF_TURN_MS + (index % columns) * SHELF_STAGGER_MS, (t) => {
        actor.root.position.lerpVectors(start, end, t);
        actor.root.scale.setScalar(0.9 + 0.1 * t);
      }, ease.out, actor);
    });
    if (held >= 0 && this.cases.size > 0) {
      const ids = [...this.cases.keys()];
      this.core.selectApp(ids[Math.min(held, ids.length - 1)]!);
    }
  }

  /** Turn the rack one page back or on. */
  turnShelf(by: -1 | 1): void {
    this.showPage(this.shelfPage + by);
  }

  /** Turn the rack to the page a game is on, when it is on another. */
  private revealCase(app: string): void {
    const page = pageOf(shelfOrder(this.core.state.snapshot.apps), app, this.room.anchors.appSlots.length);
    if (page >= 0 && page !== this.shelfPage) {
      this.shelfPage = page;
      this.syncCases(this.core.state.snapshot.apps);
    }
  }

  /** Where the rack's pager sits: on the lower edge of the rack, in the middle, a little in front of the cases. */
  private pagerPoint(): THREE.Vector3 {
    const slots = this.room.anchors.appSlots;
    const point = new THREE.Vector3();
    let low = Infinity;
    for (const slot of slots) {
      point.x += slot.position[0]! / slots.length;
      point.z += slot.position[2]! / slots.length;
      low = Math.min(low, slot.position[1]!);
    }
    const yaw = slots[0]!.yaw;
    return point.setY(low - SHELF_PAGER_DROP).add(new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(0.1));
  }

  // ------------------------------------------------------------- desktop

  private async bootDesktop(): Promise<void> {
    try {
      const { mountStudioDesktop } = await import("../shell/system-host.ts");
      const desktop = await mountStudioDesktop({ baseUrl: this.options.desktopUrl });
      this.desktop = desktop;
      this.laptop.show(desktop.canvas);
      desktop.onFrame(() => (this.desktopDirty = true));
      desktop.onIntent((intent) => {
        if (intent.t === "play") this.play(intent.app, intent.device);
        else if (intent.t === "skin") void this.setSkin(intent.device, intent.skin);
        else if (intent.t === "room") void this.setRoom(intent.room);
        else if (intent.t === "publish") void this.core.setPublished(intent.app, intent.on);
        else if (intent.t === "membership") this.membership();
        else if (intent.t === "opened") track("laptop.app_opened", { props: { program: intent.program } });
        else if (intent.t === "signout") void this.signOut();
      });
      desktop.setRate(this.core.state.focus.kind === "laptop" ? "full" : "idle");
      desktop.publish({ t: "studio", snapshot: this.core.state.snapshot, focus: this.focusLine(this.core.state.focus), lang: lang() });
      this.desktopDirty = true;
    } catch (error) {
      console.warn("the laptop desktop did not start", error);
    }
  }

  // ------------------------------------------------------------- television

  private async bootTv(): Promise<void> {
    const station = new Station(this.tv, {
      runtimeUrl: this.options.runtimeUrl,
      baseUrl: this.options.tvUrl,
      onOpen: (app) => this.core.selectApp(app),
    });
    try {
      const { apps, devices, aired } = this.core.state.snapshot;
      station.setFeed(apps, devices, aired);
      await station.boot();
      this.station = station;
      station.setAudible(this.core.state.focus.kind === "tv");
    } catch (error) {
      console.warn("the television did not start", error);
    }
  }

  /** Change the game on the television, or open the one it is showing. */
  tvTurn(by: -1 | 1): void {
    this.station?.turn(by);
  }

  tvChoose(): void {
    this.station?.choose();
  }

  get tvMuted(): boolean {
    return this.station?.muted ?? false;
  }

  setTvMuted(muted: boolean): void {
    this.station?.setMuted(muted);
    this.overlay.render(this.core.state);
  }

  // ------------------------------------------------------------- intents with feedback

  /** What a guest reached for before signing in, now that the room is back with an account. */
  resume(wanted: Wanted): void {
    this.play(wanted.app, wanted.device);
  }

  /** Put a game on a device and start it. */
  play(app: string, device?: DeviceId): void {
    if (!this.core.play(app, device)) this.overlay.toast(t("toast.noBuild"));
  }

  async setSkin(device: DeviceId, skin: string, color?: string): Promise<void> {
    try {
      await this.core.setSkin(device, skin, color);
    } catch (error) {
      if (error instanceof PaymentRequired) this.overlay.paywall("colours");
      else this.overlay.toast(t("toast.colourFailed"));
      // A colour that was being previewed gives way to what the account has.
      this.skins.delete(device);
      this.syncSnapshot(this.core.state);
    }
  }

  /** Show a colour on a device while it is being mixed. Nothing is kept until setSkin. */
  previewColor(device: DeviceId, color: string): void {
    const actor = this.devices.get(device);
    if (!actor) return;
    actor.setSkin(CUSTOM_SKIN, color);
    // Whatever the next snapshot says the device wears is painted again.
    this.skins.set(device, "preview");
  }

  async setRoom(room: string): Promise<void> {
    try {
      await this.core.setRoom(room);
    } catch (error) {
      const title = this.core.state.snapshot.rooms.find((entry) => entry.id === room)?.title;
      this.overlay.toast(error instanceof PaymentRequired
        ? title ? t("toast.roomMembers", { room: title }) : t("toast.roomMembers.unnamed")
        : error instanceof Refused ? error.message : t("toast.roomFailed"));
    }
  }

  /** Open the laptop on its Settings app. */
  openSettings(): void {
    this.core.openLaptop();
    this.desktop?.launch("dev.pocket-nexus.studio.settings");
  }

  /** Open the laptop on its Install Guide at a device's section. Without a laptop, the web page's section opens in a tab. */
  openGuide(device: NativeTarget): void {
    if (!this.desktop) {
      window.open(guideUrl(device), "_blank", "noopener");
      return;
    }
    this.core.openLaptop();
    this.desktop.launch(GUIDE_PACKAGE, device);
  }

  /** Says that a package is downloading, with the way to the Install Guide's section for its device. */
  private downloading(app: string, device: NativeTarget): void {
    const title = this.core.app(app)?.title;
    this.overlay.toast(title ? t("toast.downloading", { title, device: deviceName(device) }) : t("toast.downloading.unnamed", { device: deviceName(device) }),
      { label: t("guide.title"), run: () => this.openGuide(device) });
  }

  /** The laptop's membership button: the paywall for anyone not a member, the plan's own page for a member. */
  membership(): void {
    if (!this.core.state.snapshot.membership.active) return this.overlay.paywall("member");
    this.overlay.actions.account.portal().then(
      (url) => location.assign(url),
      () => this.overlay.toast(t("account.portalFailed")),
    );
  }

  /** Download an installation package the author made public. It asks for an account: a guest is shown the sign-in card. */
  async native(app: string, device: NativeTarget): Promise<void> {
    const title = this.core.app(app)?.title ?? "";
    const askAccount = () => this.overlay.askFor(t("get.signin", { title, device: deviceName(device) }), "download");
    try {
      const file = await this.core.nativeDownload(app, device);
      // The browser fetches the file itself: a package can be hundreds of megabytes.
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.filename;
      link.click();
      this.downloading(app, device);
    } catch (error) {
      if (error instanceof SignInRequired) askAccount();
      else if (error instanceof Refused) this.overlay.toast(error.message);
      else this.overlay.toast(t("toast.downloadUnavailable"));
    }
  }

  async signOut(): Promise<void> {
    await this.core.signOut().catch(() => null);
    location.href = "/";
  }

  // ------------------------------------------------------------- state

  private onState(state: StudioState, previous: StudioState): void {
    this.overlay.render(state);
    if (state.snapshot !== previous.snapshot) {
      this.syncSnapshot(state);
    }
    // A game that is picked is on the rack: by a share link, from Pocket TV or from the room's list.
    if (state.focus.kind === "app" && !sameFocus(state.focus, previous.focus)) this.revealCase(state.focus.app);
    if (!sameFocus(state.focus, previous.focus)) {
      this.desktop?.publish({ t: "studio", snapshot: state.snapshot, focus: this.focusLine(state.focus), lang: lang() });
      void this.transition(previous.focus, state.focus);
    }
  }

  private async transition(from: Focus, to: Focus): Promise<void> {
    const token = ++this.generation;
    this.setHover(null);
    const rig = this.stage.rig;
    const keeps = to.kind === "play" ? to.device : null;

    if (from.kind === "play") {
      if (from.device !== keeps) {
        this.stopRealm();
        const actor = this.devices.get(from.device);
        if (actor) {
          actor.drawIdle("ready");
          void this.moveDevice(actor, this.slotOf(from.device)).then(() => {
            if (this.activeDevice() !== actor) this.away.delete(actor);
          });
        }
      } else if (to.kind === "play" && from.app !== to.app) {
        this.stopRealm();
      }
    }
    if (from.kind === "laptop") {
      this.desktop?.blur();
      this.desktop?.setRate("idle");
    }
    this.station?.setAudible(to.kind === "tv");

    if (to.kind !== "make") this.stage.rest(false);
    rig.swayAmount = to.kind === "room" || to.kind === "closet" ? 0.012 : to.kind === "device" || to.kind === "app" ? 0.008 : 0.004;
    switch (to.kind) {
      case "room":
        // The overview is the visitor's: their orbit and zoom apply again.
        await rig.fly(this.stage.cameraPose("overview"), 900, true);
        break;
      case "closet":
        await rig.fly(this.closetPose(), 800);
        break;
      case "device": {
        const actor = this.devices.get(to.device);
        if (!actor) break;
        // The frame is sized to the devices, so a room that draws them smaller looks closer.
        const k = this.room.deviceScale / 2;
        await rig.fly(this.beside(actor.root.localToWorld(actor.box.getCenter(new THREE.Vector3())), 1.5 * k, 0.92 * k, 1.75 * k, 0.2 * k), 800);
        break;
      }
      case "app": {
        const actor = this.cases.get(to.app);
        if (!actor) break;
        const centre = actor.headPoint().add(new THREE.Vector3(0, -CASE_H / 2, 0));
        await rig.fly(this.beside(centre, 1.25, 0.8, 1.5, 0.05), 800);
        break;
      }
      case "laptop":
        this.desktop?.setRate("full");
        await rig.fly(this.stage.cameraPose("laptop"), 900);
        break;
      case "tv":
        await rig.fly(this.withCard(this.stage.cameraPose("tv"), 0.2), 900);
        break;
      case "play":
        await this.enterPlay(to.device, to.app, token);
        break;
      case "make": {
        // The chat stands at the workbench, by the figures on it. A new app becoming one with an id is the same place.
        if (from.kind === "make") break;
        const spot = this.room.anchors.agentSpots?.claude;
        if (spot) await rig.fly(this.beside(new THREE.Vector3(...spot.position), 2.6, 1.6, 3.1, 0.7), 900);
        // The workspace covers the room: it rests while the game plays.
        if (this.core.state.focus.kind === "make") this.stage.rest(true);
        break;
      }
    }
  }

  /** A pose that looks at `centre` from the front, leaving room for the card beside it. */
  private beside(centre: THREE.Vector3, width: number, height: number, distance: number, rise: number): Pose {
    const wide = this.stage.camera.aspect > 1;
    const target = centre.clone().add(wide ? new THREE.Vector3(width * 0.2, 0, 0) : new THREE.Vector3(0, -height * 0.28, 0));
    return { target, position: target.clone().add(new THREE.Vector3(0, rise, distance)), fit: new THREE.Vector2(width, height) };
  }

  /** The shelf seen straight on, shifted so the closet panel does not cover the devices. */
  private closetPose(): Pose {
    return this.withCard(this.stage.cameraPose("devices"), 0.16);
  }

  /** Slide a pose sideways, along its own right, by `share` of its width: the card takes that side of a wide view. */
  private withCard(pose: Pose, share: number): Pose {
    if (this.stage.camera.aspect <= 1) return pose;
    const forward = new THREE.Vector3().subVectors(pose.target, pose.position).normalize();
    const shift = new THREE.Vector3().crossVectors(forward, THREE.Object3D.DEFAULT_UP).normalize().multiplyScalar(pose.fit.x * share);
    pose.target.add(shift);
    pose.position.add(shift);
    return pose;
  }

  private slotOf(device: DeviceId): DeviceAnchor {
    return this.room.anchors.deviceSlots[DEVICE_IDS.indexOf(device)]!;
  }

  /** Fly a device to an anchor. Its pose and lid blend into the way it rests there. */
  private moveDevice(actor: DeviceActor, anchor: DeviceAnchor, duration = 850): Promise<void> {
    if (actor.root.position.distanceTo(new THREE.Vector3().fromArray(anchor.position)) < 1e-4) return Promise.resolve();
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    return this.stage.tweens.run(reduced ? 0 : duration, actor.flight(anchor, this.stage.clearance), ease.inOut, actor);
  }

  private async enterPlay(device: DeviceId, appId: string, token: number): Promise<void> {
    const actor = this.devices.get(device);
    const app = this.core.app(appId);
    const build = app?.builds[device];
    if (!actor || !app || !build) {
      this.overlay.toast(t("toast.noBuildHere"));
      this.core.back();
      return;
    }
    actor.drawIdle("ready", app.title);
    this.away.add(actor);
    const spot = this.room.anchors.playSpot;
    await Promise.all([this.moveDevice(actor, spot), this.stage.rig.fly(actor.playPose(spot, this.stage.camera.aspect), 1000)]);
    if (token !== this.generation) return;
    await this.startRealm(actor, app.id, build, token);
  }

  private async startRealm(actor: DeviceActor, packageId: string, build: AppBuild, token: number): Promise<void> {
    const key = `${actor.id}|${build.js}`;
    if (this.realmKey === key) return;
    this.stopRealm();
    this.realmKey = key;
    try {
      const realm = await DeviceRealm.boot({
        untrusted: true,
        runtimeUrl: this.options.runtimeUrl,
        packageId,
        bundleUrl: build.js,
        pakUrl: build.pak,
        viewport: actor.profile.display.logical_size,
        density: actor.profile.display.raster_density,
        auxiliary: actor.profile.display.auxiliary_size,
        primary: actor.primary,
        auxiliaryCanvas: actor.auxiliary ?? undefined,
      });
      if (token !== this.generation || this.realmKey !== key) {
        realm.dispose();
        return;
      }
      this.realm = realm;
      actor.refreshScreens();
    } catch (error) {
      console.error(error);
      if (this.realmKey === key) this.realmKey = "";
      this.overlay.toast(t("toast.startFailed"));
    }
  }

  /** How a selected device is shown at its slot, asked with the device facing home. */
  private showing(device: DeviceActor, home: number, up: number): { out: number; sway: number } {
    const yaw = device.root.rotation.y;
    device.root.rotation.y = home;
    const show = device.showing(up, this.stage.clearance);
    device.root.rotation.y = yaw;
    return show;
  }

  private stopRealm(): void {
    this.realm?.dispose();
    this.realm = null;
    this.realmKey = "";
    this.press = null;
  }

  private activeDevice(): DeviceActor | null {
    const focus = this.core.state.focus;
    return focus.kind === "play" ? this.devices.get(focus.device) ?? null : null;
  }

  // ------------------------------------------------------------- frame

  private frame(dt: number): void {
    if (this.shelfPages > 1) {
      // Beside a game's card the view is close to the rack, and its lower edge may be past the window's: the pager stays in reach.
      const at = this.stage.project(this.pagerPoint());
      this.overlay.shelfAt(at.visible || this.core.state.focus.kind === "app" ? at : null);
    }
    const actor = this.activeDevice();
    if (this.realm && actor && this.realm.advance(dt)) actor.refreshScreens();
    if (this.desktopDirty) {
      this.desktopDirty = false;
      this.laptop.refresh();
    }
    this.station?.advance(dt);
    this.tv.update(dt);
    // Hover feedback eases toward its goal: devices rise, cases slide out.
    const k = 1 - Math.exp(-dt / 90);
    const focus = this.core.state.focus;
    this.clock += dt;
    const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
    for (const device of this.devices.values()) {
      const selected = focus.kind === "device" && focus.device === device.id;
      const hovered = this.hovered && "device" in this.hovered && this.hovered.device === device && focus.kind !== "device";
      const scale = this.room.deviceScale;
      // A selected device comes off its stand, far enough out that its turn
      // from side to side touches nothing behind it. The room's own shape
      // decides how far.
      const home = this.slotOf(device.id).yaw;
      const resting = !this.away.has(device);
      const show = selected && resting ? this.showing(device, home, 0.02 * scale) : null;
      const hold = this.holds.get(device) ?? { up: 0, out: 0 };
      hold.up = THREE.MathUtils.lerp(hold.up, selected ? 0.02 * scale : hovered ? 0.0125 * scale : 0, k);
      hold.out = THREE.MathUtils.lerp(hold.out, show?.out ?? 0, k);
      this.holds.set(device, hold);
      device.hold(hold.up, hold.out);
      if (!resting) continue;
      // It turns only once it is most of the way out.
      const ready = show && hold.out > show.out * 0.8 - 1e-4;
      const yaw = ready && !still ? home + Math.sin(this.clock / 1100) * show.sway : home;
      device.root.rotation.y = THREE.MathUtils.lerp(device.root.rotation.y, yaw, 1 - Math.exp(-dt / 260));
    }
    let stamp = 0;
    for (const device of this.devices.values()) {
      const { position, rotation } = device.root;
      const hold = this.holds.get(device);
      stamp += position.x * 3 + position.y * 5 + position.z * 7 + rotation.y * 11 + (hold?.up ?? 0) * 13 + (hold?.out ?? 0) * 29;
    }
    for (const game of this.cases.values()) {
      const selected = focus.kind === "app" && focus.app === game.app.id;
      const goal = selected ? 1 : this.hovered && "game" in this.hovered && this.hovered.game === game ? 0.3 : 0;
      const value = THREE.MathUtils.lerp(this.pulls.get(game) ?? 0, goal, k);
      this.pulls.set(game, value);
      game.pull(value);
      stamp += value * 17 + game.root.position.x * 19 + game.root.position.y * 23;
    }
    if (!(Math.abs(stamp - this.castStamp) < 1e-6)) {
      this.castStamp = stamp;
      this.stage.refreshShadows();
    }
  }

  // ------------------------------------------------------------- input

  private aim(event: PointerEvent, layer: number): THREE.Raycaster {
    const rect = this.stage.canvas.getBoundingClientRect();
    const point = new THREE.Vector2(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(point, this.stage.camera);
    this.raycaster.layers.set(layer);
    return this.raycaster;
  }

  private targetAt(event: PointerEvent): Target | null {
    const boxes = [
      ...[...this.devices.values()].map((d) => d.hitbox), ...[...this.cases.values()].map((c) => c.hitbox),
      this.laptop.hitbox, this.tv.hitbox,
    ];
    const hit = this.aim(event, LAYER_SELECT).intersectObjects(boxes, false)[0];
    if (!hit) return null;
    const data = hit.object.userData;
    if (data.device) return { device: data.device as DeviceActor };
    if (data.case) return { game: data.case as CaseActor };
    if (data.tv) return { tv: true };
    return { laptop: true };
  }

  private partAt(event: PointerEvent, actor: DeviceActor): (StagePart & { uv?: THREE.Vector2 }) | null {
    const hit = this.aim(event, LAYER_INPUT).intersectObjects(actor.proxies.children, false)[0];
    return hit ? { ...(hit.object.userData.part as StagePart), uv: hit.uv } : null;
  }

  private setHover(target: Target | null, event?: PointerEvent): void {
    this.hovered = target;
    this.stage.canvas.style.cursor = target ? "pointer" : "";
    if (!target || !event) {
      this.overlay.hover(null);
      return;
    }
    const [text, point] = "device" in target
      ? [deviceName(target.device.id), target.device.headPoint()]
      : "game" in target
        ? [target.game.app.title, target.game.headPoint()]
        : "tv" in target
          ? ["Pocket TV", this.tv.head.clone()]
          : [t("room.laptop"), this.laptop.centre.clone().add(new THREE.Vector3(0, 0.2, 0))];
    const at = this.stage.project(point);
    this.overlay.hover(text, at.x, at.y);
  }

  private onPointerMove(event: PointerEvent): void {
    const focus = this.core.state.focus;
    const drag = this.drag;
    if (drag && drag.pointerId === event.pointerId) {
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      // Past a few pixels the press is a drag: in the overview it turns the room.
      if (!drag.moved && Math.hypot(dx, dy) < 5) return;
      drag.moved = true;
      drag.x = event.clientX;
      drag.y = event.clientY;
      if (focus.kind === "room") {
        this.stage.rig.orbit(dx, dy);
        this.stage.canvas.style.cursor = "grabbing";
        this.overlay.hover(null);
      }
      return;
    }
    this.stage.rig.pointer((event.clientX / innerWidth) * 2 - 1, (event.clientY / innerHeight) * 2 - 1);
    if (focus.kind === "laptop") {
      const at = this.laptop.pick(this.aim(event, 0));
      if (at && this.desktop) this.desktop.pointer(at[0], at[1], this.laptopDown);
      return;
    }
    const actor = this.activeDevice();
    if (actor && this.realm) {
      const part = this.partAt(event, actor);
      if (this.press?.part.touch_surface) {
        if (part?.touch_surface === this.press.part.touch_surface && part.uv && part.logical_size) {
          this.realm.touch(part.touch_surface, [part.uv.x * part.logical_size[0], (1 - part.uv.y) * part.logical_size[1]]);
        }
        return;
      }
      this.stage.canvas.style.cursor = part ? "pointer" : "";
      return;
    }
    if (focus.kind === "play") return;
    const target = this.targetAt(event);
    // The selected thing does not announce itself again.
    const selected = (focus.kind === "device" && target && "device" in target && target.device.id === focus.device)
      || (focus.kind === "app" && target && "game" in target && target.game.app.id === focus.app)
      || (focus.kind === "tv" && target && "tv" in target);
    this.setHover(selected ? null : target, event);
  }

  private onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 && event.button !== 2) return;
    const focus = this.core.state.focus;
    if (focus.kind === "laptop") {
      const at = this.laptop.pick(this.aim(event, 0));
      if (at && this.desktop) {
        this.laptopDown = event.button === 0;
        this.stage.canvas.setPointerCapture(event.pointerId);
        this.desktop.pointer(at[0], at[1], true, event.button === 2 ? 2 : 1, event.shiftKey);
      }
      return;
    }
    const actor = this.activeDevice();
    if (actor && this.realm) {
      const part = this.partAt(event, actor);
      if (!part) return this.untouched(event, actor);
      if (this.press) return;
      const bit = part.button ? BUTTON_BITS[part.button] ?? 0 : 0;
      if (!bit && !part.touch_surface) return;
      this.press = { pointerId: event.pointerId, part, bit, tick: this.realm.ticks };
      this.stage.canvas.setPointerCapture(event.pointerId);
      if (part.touch_surface && part.uv && part.logical_size) {
        this.realm.touch(part.touch_surface, [part.uv.x * part.logical_size[0], (1 - part.uv.y) * part.logical_size[1]]);
      } else {
        this.realm.press(bit, true);
      }
      return;
    }
    if (focus.kind === "play" || event.button !== 0) return;
    // Selecting waits for the release, so the same press can become a drag.
    this.drag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    this.stage.canvas.setPointerCapture(event.pointerId);
  }

  /**
   * A press on a running device that met no key and no surface that takes
   * touch. On a screen that takes none (the PSP's, the 3DS's upper screen) the
   * room says so, once, and again only a few seconds after.
   */
  private untouched(event: PointerEvent, actor: DeviceActor): void {
    if (event.button !== 0) return;
    const screen = actor.screenAt(this.aim(event, 0));
    const hint = screen ? noTouchHint({ ...deviceEntry(actor.id), short: deviceShort(actor.id) }, screen, lang()) : null;
    if (hint && this.touchHint.due(performance.now())) this.overlay.toast(hint);
  }

  /** A press released without moving: pick what is under it, or step back. */
  private click(event: PointerEvent): void {
    const target = this.targetAt(event);
    // At the television, a click on its picture opens the game it is showing.
    if (this.core.state.focus.kind === "tv" && this.tv.pick(this.aim(event, 0))) {
      this.station?.choose();
    } else if (!target) {
      this.core.back();
    } else if ("device" in target) {
      this.core.selectDevice(target.device.id);
    } else if ("game" in target) {
      this.core.selectApp(target.game.app.id);
    } else if ("tv" in target) {
      this.core.openTv();
    } else {
      this.core.openLaptop();
    }
  }

  private onPointerUp(event: PointerEvent): void {
    const drag = this.drag;
    if (drag && drag.pointerId === event.pointerId) {
      this.drag = null;
      this.stage.canvas.style.cursor = "";
      if (!drag.moved && event.type === "pointerup") this.click(event);
      return;
    }
    if (this.core.state.focus.kind === "laptop") {
      const at = this.laptop.pick(this.aim(event, 0));
      if (this.desktop && (at || this.laptopDown)) {
        const [x, y] = at ?? [0, 0];
        this.desktop.pointer(x, y, false, event.button === 2 ? 2 : 1, event.shiftKey);
      }
      this.laptopDown = false;
      return;
    }
    const press = this.press;
    const realm = this.realm;
    if (!press || press.pointerId !== event.pointerId || !realm) return;
    const release = () => {
      if (this.press !== press) return;
      this.press = null;
      if (press.part.touch_surface) realm.touch(press.part.touch_surface, null);
      else realm.press(press.bit, false);
    };
    // Hold the input until the guest has run at least one frame with it.
    if (realm.ticks > press.tick) release();
    else realm.afterNextTick(release);
  }

  /**
   * Beside a game's card the arrow keys move along the rack: Left and Right to
   * the game before or after it, Up and Down to the game above or below it.
   * True when the key is one of them. A key held down moves once.
   */
  private shelfKey(event: KeyboardEvent): boolean {
    const step = SHELF_KEYS[event.code];
    if (!step || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return false;
    event.preventDefault();
    if (!event.repeat) this.stepShelf(step);
    return true;
  }

  /**
   * Open the card of the game a step along the rack lands on, as a click on
   * its case does. A game on another page turns the rack to it first.
   */
  private stepShelf(step: ShelfStep): void {
    const focus = this.core.state.focus;
    if (focus.kind !== "app") return;
    const slots = this.room.anchors.appSlots;
    const ordered = shelfOrder(this.core.state.snapshot.apps);
    const places = slots.map((slot) => ({ along: slot.position[0]! * Math.cos(slot.yaw) - slot.position[2]! * Math.sin(slot.yaw), up: slot.position[1]! }));
    const next = shelfStep(ordered, focus.app, step, places);
    if (!next) return;
    this.showPage(pageOf(ordered, next, slots.length));
    this.core.selectApp(next);
  }

  private onKey(event: KeyboardEvent, down: boolean): void {
    if (event.target instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName)) return;
    const focus = this.core.state.focus;
    if (down && event.key === "Escape" && focus.kind !== "room") {
      this.core.back();
      event.preventDefault();
      return;
    }
    // Page Up and Page Down turn the rack from the overview and from a game's card.
    if (down && !event.repeat && (focus.kind === "room" || focus.kind === "app") && (event.code === "PageDown" || event.code === "PageUp")) {
      this.turnShelf(event.code === "PageDown" ? 1 : -1);
      event.preventDefault();
      return;
    }
    if (down && focus.kind === "app" && this.shelfKey(event)) return;
    if (focus.kind === "laptop" && this.desktop) {
      if (down) {
        if (this.desktop.keyDown(event)) event.preventDefault();
      } else {
        this.desktop.keyUp(event);
      }
      return;
    }
    if (focus.kind === "tv" && this.station) {
      if (!down || event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.code === "ArrowLeft") this.station.turn(-1);
      else if (event.code === "ArrowRight") this.station.turn(1);
      else if (event.code === "Enter" || event.code === "KeyZ") this.station.choose();
      else return;
      event.preventDefault();
      return;
    }
    const realm = this.realm;
    if (!realm || event.metaKey || event.ctrlKey || event.altKey) return;
    const bit = KEY_BITS[event.code];
    if (!bit) return;
    event.preventDefault();
    if (down) {
      if (!event.repeat) this.keyTicks.set(event.code, realm.ticks);
      realm.press(bit, true);
      return;
    }
    // A tap shorter than one frame is held until the guest has seen it.
    const since = this.keyTicks.get(event.code) ?? -1;
    this.keyTicks.delete(event.code);
    if (realm.ticks > since) realm.press(bit, false);
    else realm.afterNextTick(() => realm.press(bit, false));
  }
}
